/**
 * Document Service and Types
 *
 * This service provides document management functionality including:
 * - Creating (uploading) multiple documents
 * - Updating document metadata (not yet implemented on backend)
 * - Deleting documents
 * - Fetching documents with filtering
 *
 * Usage with hooks:
 *
 * ```typescript
 * import { useCreateDocument, useDeleteDocument, useDocuments } from '@hooks/api';
 *
 * const MyComponent = () => {
 *   const createDocument = useCreateDocument();
 *   const deleteDocument = useDeleteDocument();
 *   const { data: documents } = useDocuments({ projectIds: ['project-id'] });
 *
 *   const handleUpload = async (files: File[]) => {
 *     await createDocument.mutateAsync({
 *       files,
 *       projectId: 'project-id',
 *       category: 'PERMIT' // or 'CONTRACT', 'INVOICE', etc.
 *     });
 *   };
 *
 *   const handleDelete = async (documentId: string) => {
 *     await deleteDocument.mutateAsync({ documentId });
 *   };
 *
 *   return (
 *     // Your component JSX
 *   );
 * };
 * ```
 */

import { Document } from '@models/document.model';
import { graphQlClient, graphQlMultipleUpload } from '@config/graphqlClient';
import DocumentsGraphQL from '@graphql/document.queries';

export type CreateDocumentPayload = {
  files: File[];
  projectId?: string;
  category: string;
  // Note: taskId and delegateOrganizationId are not supported in DocumentCreateInput
  // They exist in the Document model but are not used for creation
};

export type UpdateDocumentPayload = {
  documentId: string;
  name?: string;
  category?: string;
  taskId?: string;
  // Note: projectId and delegateOrganizationId updates may not be supported
  // depending on the backend GraphQL schema for DocumentUpdateInput
};

export type DeleteDocumentPayload = {
  documentId: string;
};

export const documentService = {
  async createDocuments(payload: CreateDocumentPayload): Promise<Document[]> {
    const { files, projectId, category } = payload;

    if (!files || files.length === 0) {
      throw new Error('Files are required');
    }

    const documentInput = {
      url: '', // This will be set by the upload service
      projectId: projectId || '',
      category,
    };

    try {
      const response = await graphQlMultipleUpload(
        files,
        'document',
        DocumentsGraphQL.mutations.addDocument,
        documentInput,
      );

      return response as Document[];
    } catch (error) {
      console.error('Error creating documents:', error);
      throw error;
    }
  },

  async updateDocument(payload: UpdateDocumentPayload): Promise<Document> {
    const { documentId, ...updateData } = payload;

    if (!documentId) {
      throw new Error('documentId is required');
    }

    try {
      const data: any = await graphQlClient.request(
        DocumentsGraphQL.mutations.updateDocument,
        {
          document: {
            documentId,
            ...updateData,
          },
        },
      );

      return data.updateDocument;
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  },

  async deleteDocument(payload: DeleteDocumentPayload) {
    const { documentId } = payload;

    if (!documentId) {
      throw new Error('documentId is required');
    }

    try {
      const data: any = await graphQlClient.request(
        DocumentsGraphQL.mutations.deleteDocument,
        { id: documentId },
      );

      return data.deleteDocument;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  },

  async getDocuments(params?: {
    projectIds?: string[];
    taskIds?: string[];
    name?: string;
    category?: string;
    page?: number;
    limit?: number;
  }) {
    try {
      const data: any = await graphQlClient.request(
        DocumentsGraphQL.queries.getDocuments,
        params,
      );

      return data.getDocuments;
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  },
};

export default documentService;
